import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SEO from '../components/SEO';
import PageShell from '../components/site/PageShell';
import Lightbox from '../components/site/Lightbox';
import { images } from '../config/esanzaContent';

const galleryImages = [
  { src: '/images/esanza/platform-gallery/dashboard-01.png', label: 'Farmer Dashboard' },
  { src: '/images/esanza/platform-gallery/dashboard-02.png', label: 'Tree Management' },
  { src: '/images/esanza/platform-gallery/dashboard-03.png', label: 'Field Overview' },
  { src: '/images/esanza/platform-gallery/dashboard-04.png', label: 'Training Hub' },
  { src: '/images/esanza/platform-gallery/dashboard-05.png', label: 'Analytics View' },
];

const GalleryPage: React.FC = () => {
  const { t } = useTranslation();
  const [activeIdx, setActiveIdx] = useState<number | null>(null);

  const open = useCallback((i: number) => setActiveIdx(i), []);
  const close = useCallback(() => setActiveIdx(null), []);
  const prev = useCallback(() => setActiveIdx((i) => (i !== null ? (i - 1 + galleryImages.length) % galleryImages.length : null)), []);
  const next = useCallback(() => setActiveIdx((i) => (i !== null ? (i + 1) % galleryImages.length : null)), []);

  const active = activeIdx !== null ? galleryImages[activeIdx] : null;

  return (
    <>
      <SEO
        title="Platform Gallery | eSanza"
        description="Explore the eSanza platform through interactive screenshots of dashboard features, farmer management, traceability, and analytics."
        imagePath={images.app}
      />
      <PageShell>
        {/* ── Hero ─────────────────────────────────────── */}
        <section className="relative overflow-hidden px-5 pb-16 pt-32 lg:px-8">
          <div className="absolute inset-0">
            <img src={images.app} alt="" className="h-full w-full object-cover opacity-15" />
            <div className="absolute inset-0 bg-[linear-gradient(90deg,#06150e_0%,rgba(6,21,14,.96)_50%,rgba(6,21,14,.65)_100%)]" />
          </div>

          <div className="relative mx-auto max-w-7xl text-center">
            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-xs font-extrabold uppercase tracking-[0.24em] text-[#d4af37]"
            >
              {t('site.gallery.label', 'Platform gallery')}
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="mx-auto mt-5 max-w-4xl text-5xl font-black leading-[1] tracking-tight md:text-7xl"
            >
              {t('site.gallery.title', 'See the platform in action.')}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-white/70"
            >
              {t('site.gallery.subtitle', 'Screenshots from the eSanza dashboard — farmer management, tree tracking, training, and analytics.')}
            </motion.p>
          </div>
        </section>

        {/* ── Gallery Grid ─────────────────────────────── */}
        <section className="bg-[#f7f9f4] px-5 py-24 text-[#102016] lg:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {galleryImages.map((img, i) => (
                <motion.button
                  key={img.src}
                  onClick={() => open(i)}
                  initial={{ opacity: 0, y: 40, scale: 0.95 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.55, delay: i * 0.08 }}
                  whileHover={{ y: -8, scale: 1.025 }}
                  className="group relative overflow-hidden rounded-2xl border border-[#dbe5d2] bg-white shadow-lg shadow-[#102016]/[0.06] transition-shadow hover:shadow-2xl hover:shadow-[#d4af37]/15 cursor-pointer"
                >
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#06150e]/40 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100 z-10" />

                  <div className="overflow-hidden">
                    <img
                      src={img.src}
                      alt={img.label}
                      className="h-64 w-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                  </div>
                </motion.button>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mt-16 flex flex-wrap items-center justify-center gap-4"
            >
              <Link
                to="/platform"
                className="inline-flex items-center gap-2 rounded-full bg-[#102016] px-8 py-4 text-sm font-extrabold text-white transition hover:bg-[#d4af37] hover:text-[#06150e]"
              >
                Platform overview
                <span className="transition group-hover:translate-x-1">→</span>
              </Link>
              <Link
                to="/try-esanza"
                className="inline-flex items-center gap-2 rounded-full border border-[#dbe5d2] px-8 py-4 text-sm font-extrabold text-[#102016] transition hover:bg-[#102016] hover:text-white"
              >
                Try eSanza
              </Link>
            </motion.div>
          </div>
        </section>
      </PageShell>

      <AnimatePresence>
        {active && (
          <Lightbox
            image={active}
            index={activeIdx!}
            total={galleryImages.length}
            images={galleryImages}
            onPrev={prev}
            onNext={next}
            onClose={close}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default GalleryPage;
